import { Injectable, inject } from '@angular/core';

import { SportsCacheService } from '@frontend/sports/betting-offer/feature/sports-cache';
import { TopNavigationConfig } from '@frontend/sports/common/client-config-data-access';
import { ItemType, isItemType } from '@frontend/sports/common/core/data-access/sport-model';
import { TopMenuContentItem } from '@frontend/sports/navigation/core/feature/models';
import { SessionStoreService } from '@frontend/vanilla/core';
import { PLATFORM, linkedTransferState } from '@frontend/vanilla/ssr';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';

import { NavigationItemsMapper } from '../navigation-items-mapper';
import { NavigationRouterService } from '../navigation-router.service';

@Injectable({ providedIn: 'root' })
export class LastSelectedSportProvider {
    private sessionStore = inject(SessionStoreService);
    private navigationRouter = inject(NavigationRouterService);
    private sportService = inject(SportsCacheService);
    private mapper = inject(NavigationItemsMapper);
    private navigationConfig = inject(TopNavigationConfig);

    readonly navigationItemsTransferState = linkedTransferState<TopMenuContentItem | undefined>('last-selected-navigation-items', undefined);

    private readonly lastSport = new BehaviorSubject<TopMenuContentItem | undefined>(this.navigationItemsTransferState());

    private readonly storeKey: string = 'navigation-last-sport';
    isServer = inject(PLATFORM).isServer;

    lastSport$: Observable<TopMenuContentItem | undefined> = this.lastSport.asObservable();

    constructor() {
        if (this.lastSelectedSport) {
            this.mapSport(this.lastSelectedSport);
        }

        this.navigationRouter.selectedSport$.subscribe((sportId) => this.mapSport(sportId));
    }

    private set sport(sport: number) {
        this.sessionStore.set(this.storeKey, sport);
    }

    private get lastSelectedSport(): number | undefined {
        return this.sessionStore.get<number>(this.storeKey) || undefined;
    }

    private mapSport(sportId: number): void {
        this.sport = sportId;

        combineLatest([this.sportService.getSportList(), this.navigationConfig.whenReady])
            .pipe(
                map(([sports]) => {
                    const sportItem = sports.find((sport) => sport.id === sportId);

                    if (isItemType(sportItem, ItemType.Sport)) {
                        const hasSeparator = this.navigationConfig.sportsConfig?.selectedSport.useBeforeSeparator;

                        const currentSport = this.mapper.buildSportMenuItem({
                            id: sportId,
                            name: sportItem.name,
                            separator: hasSeparator,
                            isVirtual: sportItem.isVirtual,
                        });
                        this.lastSport.next(currentSport);
                        if (this.isServer) {
                            this.navigationItemsTransferState.set(currentSport);
                        }
                    }
                }),
            )
            .subscribe();
    }
}
